<template>
  <button
    v-capture-button-name="props.captureButtonName"
    type="button"
    class="access-code-card flex w-full items-center gap-1 rounded-2xl border p-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60"
    :class="cardClasses"
    @click="copyToClipboard"
  >
    <div class="min-w-0 flex-1">
      <p class="typo-text2 truncate tracking-[-0.02em] text-black">
        {{ resolvedMessage }}
      </p>
      <p class="typo-title4 mt-1 truncate tracking-[-0.02em] text-secondary-dark">
        {{ code }}
      </p>
    </div>

    <span
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm transition-colors duration-150"
      :class="iconContainerClasses"
    >
      <Icon :icon="copied ? IconName.Checkmark : IconName.Link" class="text-[20px]" />
    </span>
  </button>
</template>

<script setup lang="ts">
import { Icon } from '@kiplin/web-common/design-system';
import { IconName } from '@kiplin/web-common/icon';
import type { AccessCodeCardProps } from '@client/entity/client/application/component/AccessCodeCard/AccessCodeCard.types';
import { computed, onBeforeUnmount, ref } from 'vue';
import { AccessCodeCopyError } from '@client/entity/client/domain/error/access-code-copy.error';

const props = defineProps<AccessCodeCardProps>();
const emit = defineEmits<{ copied: [value: string] }>();

const copied = ref(false);
let copiedTimeout: ReturnType<typeof setTimeout> | undefined;

const clearCopiedTimeout = () => {
  if (!copiedTimeout) {
    return;
  }

  clearTimeout(copiedTimeout);
  copiedTimeout = undefined;
};

const scheduleCopiedReset = () => {
  clearCopiedTimeout();
  copiedTimeout = setTimeout(() => {
    copied.value = false;
    copiedTimeout = undefined;
  }, 2000);
};

const resolvedMessage = computed(() => (copied.value ? props.copiedFeedback : props.label));
const cardClasses = computed(() =>
  copied.value
    ? 'border-secondary bg-secondary-light hover:border-secondary'
    : 'border-grey-light bg-white hover:border-secondary',
);
const iconContainerClasses = computed(() =>
  copied.value ? 'bg-secondary text-secondary-dark' : 'bg-secondary-light text-secondary-dark',
);

onBeforeUnmount(() => {
  clearCopiedTimeout();
});

const copyUsingClipboardItem = async (value: string) => {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    throw new Error('ClipboardItem API not supported');
  }

  const clipboardItem = new ClipboardItem({ 'text/plain': new Blob([value], { type: 'text/plain' }) });
  await navigator.clipboard.write([clipboardItem]);
};

const copyToClipboard = async () => {
  if (!props.code) {
    return;
  }

  try {
    await navigator.clipboard.writeText(props.code);
  } catch (error) {
    try {
      await copyUsingClipboardItem(props.code);
    } catch (fallbackError) {
      const copyError = fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError));
      console.error(new AccessCodeCopyError(copyError));
      return;
    }
  }

  copied.value = true;
  scheduleCopiedReset();
  emit('copied', props.code);
};
</script>
